import type { LinkInfo } from "@gsignatures/core";
import { useEffect, useRef, useState } from "react";
import { isFailure, type SignerApi } from "../api/types";
import { Icon } from "../components/Icon";
import { TIMING } from "../config";
import { who } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import { S } from "../strings";

interface Props {
  api: SignerApi;
  link: LinkInfo;
  onVerified: () => void;
  /** Five wrong codes: the link is paused. */
  onPaused: () => void;
  /** Anything this screen doesn't handle itself (dead link, offline…). */
  onError: (e: unknown, retry?: () => void) => void;
}

const EMPTY = ["", "", "", "", "", ""];

export function VerifyScreen({ api, link, onVerified, onPaused, onError }: Props) {
  const w = who(link.sender);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [digits, setDigits] = useState<string[]>(EMPTY);
  const [status, setStatus] = useState<"idle" | "checking" | "bad" | "ok">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState<number>(0);
  const [now, setNow] = useState(() => Date.now());
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const busy = useRef(false);
  const heading = useFocusHeading(sent ? "code" : "intro");

  useEffect(() => {
    if (sent) refs.current[0]?.focus();
  }, [sent]);

  useEffect(() => {
    if (!sent) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [sent]);

  async function sendCode(again = false) {
    setSending(true);
    setMessage(null);
    try {
      const r = await api.sendCode();
      // The server's clock decides; a phone whose clock is off can't push the wait past two minutes.
      const at = new Date(r.resendAt).getTime();
      setResendAt(Number.isNaN(at) ? 0 : Math.min(at, Date.now() + 120_000));
      setNow(Date.now());
      setSent(true);
      if (again) {
        setDigits(EMPTY);
        setStatus("idle");
        setMessage(S.verify.resent);
        refs.current[0]?.focus();
      }
    } catch (e) {
      // A code went out moments ago (e.g. the page was reloaded): go to the boxes instead of standing still.
      const just = isFailure(e) && e.code === "rate_limited" ? (e.details as { resendAt?: string } | undefined)?.resendAt : undefined;
      if (just) {
        const at = new Date(just).getTime();
        setResendAt(Number.isNaN(at) ? 0 : Math.min(at, Date.now() + 120_000));
        setNow(Date.now());
        setSent(true);
        setMessage(S.verify.alreadySent);
      } else if (isFailure(e) && e.code === "rate_limited") setMessage(S.errors.rateLimited);
      else onError(e, () => void sendCode(again));
    } finally {
      setSending(false);
    }
  }

  async function submit(ds: string[]) {
    const code = ds.join("");
    if (code.length < 6 || busy.current) return;
    busy.current = true;
    setStatus("checking");
    setMessage(null);
    try {
      const r = await api.verify(code);
      if (r.ok) {
        setStatus("ok");
        setTimeout(onVerified, TIMING.verifiedMs);
        return;
      }
      busy.current = false;
      if (r.reason === "paused") {
        onPaused();
        return;
      }
      setStatus("bad");
      setMessage(r.reason === "expired" ? S.verify.expired : S.verify.wrong(r.triesLeft ?? 0));
      if (r.reason === "expired") setResendAt(0);
      setTimeout(() => {
        setDigits(EMPTY);
        setStatus("idle");
        refs.current[0]?.focus();
      }, TIMING.shakeMs);
    } catch (e) {
      busy.current = false;
      setStatus("idle");
      if (isFailure(e) && e.code === "rate_limited") setMessage(S.errors.rateLimited);
      else onError(e, () => void submit(ds));
    }
  }

  function fill(from: number, text: string) {
    const clean = text.replace(/\D/g, "").slice(0, 6);
    if (!clean) return;
    const start = clean.length >= 6 ? 0 : from;
    const ds = digits.slice();
    for (let k = 0; k < clean.length && start + k < 6; k++) ds[start + k] = clean[k]!;
    setDigits(ds);
    const nextEmpty = ds.findIndex((d) => !d);
    refs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
    void submit(ds);
  }

  function onChange(i: number, raw: string) {
    const only = raw.replace(/\D/g, "");
    if (only.length > 1) {
      fill(i, only);
      return;
    }
    const ds = digits.slice();
    ds[i] = only;
    setDigits(ds);
    if (only && i < 5) refs.current[i + 1]?.focus();
    void submit(ds);
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const ds = digits.slice();
      ds[i - 1] = "";
      setDigits(ds);
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    }
  }

  const secs = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const ok = status === "ok";

  if (!sent) {
    const intro = S.verify.intro(w, link.signer.maskedEmail);
    return (
      <main className="page" id="main">
        <div className="panel panel-pad center-card">
          <div className="badge-ico">
            <Icon n="lock" />
          </div>
          <h1 className="title" ref={heading} tabIndex={-1}>
            {S.verify.title}
          </h1>
          <p className="lede">
            {intro.before}
            <b>{intro.email}</b>
            {intro.after}
          </p>
          <div className="cta-row">
            <button type="button" className="btn btn-primary btn-wide" onClick={() => void sendCode()} disabled={sending} aria-busy={sending}>
              <Icon n="mail" /> {sending ? S.verify.sending : S.verify.sendCode}
            </button>
          </div>
          <p className="msg-line" aria-live="polite">
            {message && <span className="err-text">{message}</span>}
          </p>
          <p className="foot-note">{S.verify.notYou(link.signer.firstName)}</p>
        </div>
      </main>
    );
  }

  const sentTo = S.verify.sentTo(link.signer.maskedEmail);
  return (
    <main className="page" id="main">
      <div className="panel panel-pad center-card">
        <div className={`badge-ico ${ok ? "ok" : ""}`}>
          <Icon n={ok ? "check" : "lock"} />
        </div>
        <h1 className="title" ref={heading} tabIndex={-1}>
          {ok ? S.verify.verified : S.verify.title}
        </h1>
        <p className="lede" id="otp-help">
          {sentTo.before}
          <b>{sentTo.email}</b>
          {sentTo.after}
        </p>
        <div
          className={`otp ${status === "bad" ? "bad" : ""} ${ok ? "ok" : ""}`}
          role="group"
          aria-labelledby="otp-label"
          aria-describedby="otp-help otp-msg"
          onPaste={(e) => {
            const t = e.clipboardData.getData("text");
            if (!/\d/.test(t)) return;
            e.preventDefault();
            const at = refs.current.findIndex((r) => r === document.activeElement);
            fill(Math.max(0, at), t);
          }}
        >
          <span id="otp-label" className="sr">
            {S.verify.codeLabel}
          </span>
          {digits.map((d, i) => (
            <span key={i} className="otp-cell">
              {i === 3 && <span className="otp-gap" aria-hidden="true" />}
              <input
                ref={(el) => {
                  refs.current[i] = el;
                }}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                aria-label={S.verify.digit(i + 1)}
                value={d}
                readOnly={status === "checking" || ok}
                onChange={(e) => onChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
              />
            </span>
          ))}
        </div>
        <div className="msg-line" id="otp-msg" aria-live="polite">
          {status === "checking" ? (
            <span className="faint">{S.verify.checking}</span>
          ) : message ? (
            <span className={status === "bad" || message !== S.verify.resent ? "err-text" : "ok-text"}>{message}</span>
          ) : api.fake ? (
            <span className="faint">{S.verify.demoHint}</span>
          ) : null}
        </div>
        <div className="resend">
          {secs > 0 ? (
            <span className="faint">{S.verify.resendIn(secs)}</span>
          ) : (
            <button type="button" className="btn-link" onClick={() => void sendCode(true)} disabled={sending}>
              {S.verify.resend}
            </button>
          )}
        </div>
        <p className="foot-note">{S.verify.footer(w, link.signer.firstName)}</p>
      </div>
    </main>
  );
}
